import React from 'react';
import { useTranslation } from 'react-i18next';

import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import {
  currencySymbol,
  IExchangeRate,
  MoneyCurrency,
} from 'services/offers/exchangeRate';
import translations from 'services/translations/translations';

import 'style/offers/CurrencyToggle.css';

/**
 * A small switch of the currency shown (€ / SEK) with the exchange rate below it. Only
 * what is shown changes; without a rate only the currency of the offer can be chosen.
 */
const CurrencyToggle = ({
  base,
  onChange,
  rate,
  value,
}: {
  // The currency the figures are calculated in
  base: MoneyCurrency;
  onChange: (currency: MoneyCurrency) => void;
  rate: null | IExchangeRate;
  value: MoneyCurrency;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:currencyToggle.${key}`, options);

  return (
    <div
      className="currency-toggle"
      onClick={(event) => event.stopPropagation()}
      role="presentation"
    >
      <div
        aria-label={tr('Currency')}
        className="currency-toggle-buttons"
        role="group"
      >
        {(['EUR', 'SEK'] as MoneyCurrency[]).map((currency) => (
          <button
            className={currency === value ? 'active' : ''}
            disabled={currency !== base && rate === null}
            key={currency}
            onClick={() => onChange(currency)}
            type="button"
          >
            {currencySymbol(currency)}
          </button>
        ))}
      </div>
      <small className="currency-toggle-rate">
        {rate === null
          ? tr('No rate')
          : tr('Rate', {
              date: new Date(rate.date).toLocaleDateString('fi-FI'),
              rate: formatGroupedNumber(rate.rate, 4, 4),
              source: rate.source === 'ECB' ? tr('ECB') : rate.source,
            })}
      </small>
    </div>
  );
};

export default CurrencyToggle;
